import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { CalendarDays, Mail, Phone, UserCheck } from 'lucide-react'
import { Crest } from '@/components/generals/crest'
import { DesktopNav } from '@/components/generals/desktop-nav'
import { MobileMenu } from '@/components/generals/mobile-menu'

import { routes } from '@/constants/navigation'
import { school } from '@/constants/site'

const utilityLinkClass = 'inline-flex min-h-10 items-center gap-2 rounded-sm text-white/80 transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

export const SiteHeader: FC = (): ReactNode => {
    return (
        <>
            <div className='bg-navy-deep text-sm'>
                <div className='mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 sm:px-6'>
                    <div className='flex items-center gap-5'>
                        <a href={school.phone.href} className={utilityLinkClass}>
                            <Phone aria-hidden='true' className='size-4 text-gold' />
                            <span className='sr-only'>Call reception on </span>
                            {school.phone.display}
                        </a>

                        <a href={`mailto:${school.email}`} className={`${utilityLinkClass} hidden md:inline-flex`}>
                            <Mail aria-hidden='true' className='size-4 text-gold' />
                            <span className='sr-only'>Email the school office at </span>
                            {school.email}
                        </a>
                    </div>

                    <div className='flex items-center gap-5'>
                        <a href={school.absenceLine.href} className={utilityLinkClass}>
                            <UserCheck aria-hidden='true' className='size-4 text-gold' />
                            <span className='hidden sm:inline'>Report an absence</span>
                            <span className='sm:hidden'>Absence line</span>
                        </a>

                        <Link href={routes.termDates} className={`${utilityLinkClass} hidden lg:inline-flex`}>
                            <CalendarDays aria-hidden='true' className='size-4 text-gold' />
                            Term Dates
                        </Link>

                        <Link href={routes.vacancies} className={`${utilityLinkClass} hidden lg:inline-flex`}>
                            Vacancies
                        </Link>
                    </div>
                </div>
            </div>

            <header className='sticky top-0 z-40 border-b border-border bg-white/95 shadow-sm shadow-navy/5 supports-[backdrop-filter]:bg-white/90 supports-[backdrop-filter]:backdrop-blur'>
                <div className='relative mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-6'>
                    <Link
                        href={routes.home}
                        className='flex items-center gap-3 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-navy'
                    >
                        <Crest className='h-12 w-auto' />

                        <span className='flex flex-col'>
                            <span className='font-heading text-xl font-semibold leading-tight text-navy sm:text-[1.35rem]'>
                                {school.name}
                            </span>

                            <span className='hidden text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-gold-deep sm:block'>
                                {school.motto}
                            </span>
                        </span>
                    </Link>

                    <div className='flex h-full items-center gap-3'>
                        <DesktopNav />

                        <Link
                            href={routes.contact}
                            className='hidden min-h-11 items-center rounded-full bg-navy px-5 font-semibold text-white transition-colors hover:bg-navy-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy sm:inline-flex'
                        >
                            Contact Us
                        </Link>

                        <MobileMenu />
                    </div>
                </div>
            </header>
        </>
    )
}
